import React, { useContext, useMemo } from 'react';

import _, {
	findIndex,
	keyBy,
	pick,
	uniq,
	sortBy,
	omit,
	compact,
	values,
	head
} from 'lodash';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Input } from 'antd';

import { fal } from '@fortawesome/pro-light-svg-icons';
import { FoldableTagList } from 'components/List';

import { IconPicker } from 'components/IconPicker';
import MainRadar from 'components/Radar/MainRadar';
import AddButton from 'components/action/AddButton';
import StakeholderModal from 'components/Modal/StakeholderModal';
import { SummaryColumn } from './SummaryColumn/SummaryColumn';

import Weight from 'components/controls/Weight';
import LinksTab from './Tabs/LinksTab';
import { FishboneLinksTab } from 'components/tabs/FishboneLinksTab';
import CommentsTab from './Tabs/CommentsTab';
import GeneralTab from './Tabs/GeneralTab';

import { ConfirmModal } from 'components/Modal/ConfirmModal';

import './Navigator.sass';

import useScreenSize from 'hooks/useScreenSize';
import { remainingSpace } from 'utils/layoutUtils';

import { StakeholderModel } from 'model/StakeholderModel';
import BotTab from 'components/tabs/BotTab';
import DisabledTab from 'components/tabs/DisabledTab';

import { filterStakeholders } from './Navigator.utils';
import { getItemAlteredByScenario } from 'utils/rules';
import Tutorial from 'components/Tutorial/Tutorial';
import SubHeaderPortal from 'layout/header/SubHeaderPortal';
import { useOnboardingStep } from 'components/Onboarding/OnboardingReviewBanner';

import UIContext from 'components/Utils/UIContext';
import { MaskableComponent } from 'components/Tutorial/MaskableComponent';
import { SideMenu } from 'layout/SideMenu';
import { ExportTab } from './Tabs/ExportTab';
import { Layout } from 'antd';

// Props
interface NavigatorProps {
	stakeholders: Array<any>;
	scenario: any;
	scenarios: Array<any>;
	selection: any;
	searchTerm: String;
	setSearchTerm: Function;
	select: Function;
	deselect: Function;
	refetch: Function;
	getProject: any;
	portalContent: any;
	expanded: any;
	features: any;
	tags: any;

	stakes: any;
	indicators: any;
	risks: any;

	displayIndicators: boolean;
	displayStakes: boolean;
	displayRisks: boolean;
	onCreateTag: (name: string, callback: (tagId: string) => void) => void;

	pushModal: Function;
	popModal: Function;

	preferences: any;
	setPreferences: any;

	botThread: any;
	recentCreations: any;
	recentUpdates: any;

	executeAction: Function;

	// New handler props
	onCreateStakeholder: (values: any, defaults: any) => Promise<any>;
	onDeleteStakeholder: (id: string) => Promise<void>;
	onCreateStakeholderLink: (origin: any, target: any) => Promise<any>;
	onDeleteStakeholderLink: (stakeholderId: string, id: string) => Promise<void>;
	onChangeStakeholderLinkStrength: (link: any, strength: number) => any;
	onChangeStakeholder: (id: string, values: any) => Promise<void>;
	onUpdateStakeholderTags: (stakeholder: any, tags: any[]) => Promise<void>;
	resetBotThread?: Function;
	addBotMessage?: Function;
}

export const Navigator = (props: NavigatorProps) => {
	const params: any = useParams();
	const location = useLocation();
	const { t } = useTranslation();
	const onboarding = useOnboardingStep('stakeholders');

	const { stakeholders = [], stakes = [], indicators = [], risks = [], tags = [] } = props;

	const [editedStakeholder, setEditedStakeholder] = React.useState<
		any | null
	>(null);
	const [mode, setMode] = React.useState<any | null>({ name: 'initial' });
	// Always enable transitions for smooth spring animations
	const isTransitioning = true;

	const areAllLinksVisible = props.preferences?.pages?.navigator?.links || false
	const areTagsVisible = props.preferences?.pages?.navigator?.tags || false
	const areIndicatorsVisible = props.preferences?.pages?.navigator?.indicators || false
	const areStakesVisible = props.preferences?.pages?.navigator?.stakes || false
	const areRisksVisible = props.preferences?.pages?.navigator?.risks || false
	const areOpportunitiesVisible = props.preferences?.pages?.navigator?.opportunities || false

	const uiContext = useContext(UIContext);

	const [tagFilter, setTagFilter] = React.useState<any | null>([]);
	const [iconFilter, setIconFilter] = React.useState<any | null>(null);
	const [stakeFilter, setStakeFilter] = React.useState<any | null>(
		null
	);
	const [constraintFilter, setConstraintFilter] = React.useState<any | null>(
		null
	);

	const [riskFilter, setRiskFilter] = React.useState<any | null>(location?.state?.riskFilter || null);


	const alteredStakeholders = stakeholders.map((s: StakeholderModel) => getItemAlteredByScenario(s, props.scenario));

	let indexedRisks = keyBy(risks, 'id');

	// Usage
	let usedRisks = {};
	alteredStakeholders.forEach((s: StakeholderModel) => {
		if (s.impactedRisks) {
			s.impactedRisks.forEach((r) => {
				if (indexedRisks[r.riskId] && !indexedRisks[r.riskId].isOpportunity)
					usedRisks[r.riskId] = true;
			})
		}
	})

	let usedOpportunities = {};
	alteredStakeholders.forEach((s: StakeholderModel) => {
		if (s.impactedRisks) {
			s.impactedRisks.forEach((r) => {
				if (indexedRisks[r.riskId] && indexedRisks[r.riskId].isOpportunity)
					usedOpportunities[r.riskId] = true;
			})
		}
	})


	let usedTags = {};
	const indexedTags = keyBy(tags, 'id');
	alteredStakeholders.forEach((s: StakeholderModel) => {
		if (s.taggings) {
			s.taggings.forEach((t) => {
				if (indexedTags[t.tagId]) {
					usedTags[t.tagId] = true;
				}
			})
		}
	})

	let usedStakes = {};
	const indexedStakes = keyBy(stakes, 'id');
	alteredStakeholders.forEach((s: StakeholderModel) => {
		if (s.impactedStakes) {
			s.impactedStakes.forEach((i) => {
				if (indexedStakes[i.stakeId]) {
					usedStakes[i.stakeId] = true;
				}

			})
		}
	})

	let usedConstraints = {};
	const indexedIndicators = keyBy(indicators, 'id');
	alteredStakeholders.forEach((s: StakeholderModel) => {
		if (s.impactedIndicators) {
			s.impactedIndicators.forEach((c) => {
				if (indexedIndicators[c.indicatorId]) {
					usedConstraints[c.indicatorId] = true;
				}
			})
		}
	})

	// Removed state variables as FoldableTagList manages its own showAll state internally
	// const [showAllConstraints, setShowAllConstraints] = React.useState<any | null>(false);
	// const [showAllIndicators, setShowAllIndicators] = React.useState<any | null>(false);
	const [showAllRisks, setShowAllRisks] = React.useState<any | null>(false);
	// const [showAllOpportunities, setShowAllOpportunities] = React.useState<any | null>(false);

	const [hasRadarLegend, setHasRadarLegend] = React.useState<true | false>(
		true
	);

	const allRisks = sortBy(risks, 'name').filter(r => !r.isOpportunity);
	const allOpportunities = sortBy(risks, 'name').filter(r => r.isOpportunity);

	const filteredStakeholders = useMemo(() => filterStakeholders(
		alteredStakeholders,
		{
			name: props.searchTerm,
			tags: tagFilter,
			icon: iconFilter,
			stake: stakeFilter,
			indicator: constraintFilter,
			risk: riskFilter
		},
		props.scenario
	), [alteredStakeholders, props.searchTerm, tagFilter, iconFilter, stakeFilter, constraintFilter, riskFilter, props.scenario]);

	const hasFilters =
		props.searchTerm !== '' ||
		tagFilter.length > 0 ||
		!!iconFilter ||
		!!stakeFilter ||
		!!constraintFilter;

	const askDeleteConfirmation = async (id?: string) => {
		props.pushModal(
			<ConfirmModal
				title={t('models.item.messages.delete_confirmation')}
				t={t}
				onConfirm={() => handleDeleteStakeholder(id)}
				message={t(
					'models.item.messages.delete_confirmation_explanation'
				)}
			></ConfirmModal>
		);
	};


	// Gestion des événements clavier pour la suppression
	React.useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			// Vérifier si un stakeholder est sélectionné et si la touche Suppr/Delete est pressée
			if (props.selection && (event.key === 'Delete' || event.key === 'Backspace')) {
				// Vérifier que l'événement ne provient pas d'un champ de saisie
				const target = event.target as HTMLElement;
				if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
					return;
				}

				// Empêcher le comportement par défaut seulement si on n'est pas dans un champ de saisie
				event.preventDefault();

				askDeleteConfirmation(props.selection.id);
			}
		};

		// Ajouter l'écouteur d'événements
		document.addEventListener('keydown', handleKeyDown);

		// Nettoyer l'écouteur d'événements
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [props.selection, props.pushModal, t]);

	const screenSize = useScreenSize();
	let blocks = compact([
		{ id: 'header', verticalAlign: 'top', height: 70 },
		{ id: 'summary', horizontalAlign: 'left', width: 320 },
		{ id: 'right', horizontalAlign: 'right', width: 310 },
		{ id: 'bottom', verticalAlign: 'bottom', height: 10 }
	]);

	let remainingSpaceForCenter = remainingSpace({
		...screenSize,
		blocks
	});

	// Enable / Disable left text close to radar
	let maxWidth = Math.min(
		remainingSpaceForCenter.width,
		remainingSpaceForCenter.height
	);
	let offsetX =
		(remainingSpaceForCenter.width - maxWidth) / 2 +
		remainingSpaceForCenter.x1;
	let offsetY = remainingSpaceForCenter.y1;

	if (
		remainingSpaceForCenter.width < remainingSpaceForCenter.height ||
		offsetX < 350
	) {
		if (hasRadarLegend) {
			setHasRadarLegend(false);
		}
	} else {
		if (!hasRadarLegend) {
			setHasRadarLegend(true);
		}
	}

	const allTags: any = sortBy(tags, 'name');

	const allIcons: any = uniq(
		stakeholders.map((i: any) => (i.icon ? i.icon : 'faUser'))
	).sort();

	const indexedStakeholders = keyBy(stakeholders, 'id');

	// Create
	const handleCreateStakeholder = async (values, defaults) => {
		await props.onCreateStakeholder(values, defaults);
		if (defaults?.id) {
			if (props.selection)
				props.select(defaults);
		}
	};

	// Delete
	const handleDeleteStakeholder = async (id) => {
		await props.onDeleteStakeholder(id);
	};

	// Edit
	const handleEditStakeholder = async (id) => {
		let index = findIndex(stakeholders, (s: any) => s.id === id);
		setEditedStakeholder(stakeholders[index]);
	};

	const handleChange = async (action: string, value: string) => {
		if (!props.selection) return;

		if (action == 'trend') {
			props.onChangeStakeholder(props.selection?.id, { trend: value });
		} else if (action == 'impact') {
			props.onChangeStakeholder(props.selection?.id, { impact: value });
		} else if (action == 'edit') {
			setEditedStakeholder(props.selection);
		} else if (action == 'link') {
			setMode({ name: 'linking', origin: props.selection });
		} else if (action == 'disable') {
			await props.onChangeStakeholder(props.selection?.id, { enabled: false });
			props.deselect();
		}
	};

	const select = async (item, callback) => {
		if (mode.name == 'initial') {
			props.select(item, callback);
		} else if (mode.name == 'linking') {
			if (
				item &&
				item.id &&
				mode.origin &&
				mode.origin.id &&
				item.id !== mode.origin.id
			) {
				await props.onCreateStakeholderLink(mode.origin, item);
				await props.select(item);
			}

			setMode({ name: 'initial' });
		}
	};

	return (
		<Layout
			id="Navigator"
			className={
				'Navigator ' +
				mode.name +
				' ' +
				(props.expanded ? 'expanded' : '')
			}
		>
			<SubHeaderPortal>
				{!onboarding.active && (
					<Tutorial
						type="stakeholders"
						className="ml-auto"
						context={{ ...props, areAllLinksVisible }}
						executeAction={props.executeAction} projectId={params.id} />
				)}
			</SubHeaderPortal>

			<SummaryColumn
				isMasked={uiContext.maskedZones['summary']}
				isFiltered={hasFilters}
				onStakeClick={(i: any) => {
					stakeFilter == i.id
						? setStakeFilter(null)
						: setStakeFilter(i.id);
					props.select(null);
				}}
				onConstraintClick={(i: any) => {
					constraintFilter == i.id
						? setConstraintFilter(null)
						: setConstraintFilter(i.id);
					props.select(null);
				}}
				stakeFilter={stakeFilter}
				constraintFilter={constraintFilter}

				{...props}

				stakeholders={filteredStakeholders}
				scenario={props.scenario}
				scenarios={props.scenarios}
				pushModal={props.pushModal}
				advanced={props.displayIndicators || props.displayStakes}
				popModal={props.popModal}

				likelihoods={props.likelihoods}
				impacts={props.impacts}

				areIndicatorsVisible={areIndicatorsVisible}
				areStakesVisible={areStakesVisible}
				areRisksVisible={areRisksVisible}
				areOpportunitiesVisible={areOpportunitiesVisible}
				setPreferences={props.setPreferences}
			>

			</SummaryColumn>


			<div
				style={{
					position: 'absolute',
					top: remainingSpaceForCenter.y1 + 'px',
					left: offsetX + 'px',
					width: maxWidth + 'px',
					height: maxWidth + 'px',
					zIndex: 0
				}}
			></div>

			<Layout.Content
				className={`fw${remainingSpaceForCenter.width} x1_${remainingSpaceForCenter.x1} w${maxWidth} h${maxWidth} oX${offsetX} oY${offsetY}`}

			>
				<div style={{
					position: 'absolute',
					width: `${maxWidth}px`,
					height: `${maxWidth}px`,
					top: remainingSpaceForCenter.y1 + 'px',
					left: offsetX + 'px'
				}}>
					<MaskableComponent masked={uiContext.maskedZones['main']}>
						{maxWidth && (
							<MainRadar
								offsetX={offsetX}
								offsetY={offsetY}
								width={maxWidth}
								height={maxWidth}
								selection={props.selection}
								select={select}
								mode={mode}
								scenario={props.scenario}
								deselect={props.deselect}
								onClickLink={props.onChangeStakeholderLinkStrength}
								onChange={handleChange}

								onUpdateItem={props.onChangeStakeholder}
								displayAllLinks={areAllLinksVisible}
								isTransitioning={isTransitioning}
								isLegendVisible={hasRadarLegend}
								recentCreations={props.recentCreations}
							>{filteredStakeholders}</MainRadar>
						)}</MaskableComponent>
				</div>
			</Layout.Content>

			{uiContext.canEdit && <MaskableComponent masked={uiContext.maskedZones['add']}>
				<AddButton
					id="AddStakeholder"
					tooltip={t('models.item.actions.create_new')}
					onClick={() => {
						setEditedStakeholder({});
					}}
					className={'opened'}
				></AddButton>
			</MaskableComponent>}

			<SideMenu defaultTab={props.selection ? "general" : "filters"} tabs={(() => {
				// Calcul du nombre d'éléments désactivés
				const disabledCount = stakeholders.filter(s => {
					const altered = getItemAlteredByScenario(s, props.scenario);
					return altered.enabled === false;
				}).length;

				return [
					props.selection && {
						id: 'general',
						title: t('pages.navigator.tabs.general.title')
					},
					props.selection && {
						id: 'links',
						title: t('pages.navigator.tabs.links.title')
					},
					props.selection && props.features?.fishbone && {
						id: 'fishbone',
						title: t('pages.fishbone.title')
					},
					props.selection && {
						id: 'comments',
						title: t('pages.navigator.tabs.comments.title')
					},
					!props.selection && {
						id: 'filters',
						title: t('pages.navigator.tabs.filters.title')
					},
					!props.selection && {
						id: 'deactivated',
						title: `${t('pages.navigator.tabs.disabled.title')}${disabledCount > 0 ? ` (${disabledCount})` : ''}`
					},
					!props.selection && {
						id: 'bot',
						title: t('pages.navigator.tabs.bot.title')
					},
					{
						id: 'export',
						title: t('pages.navigator.tabs.export.title')
					},
				];
			})()}>
				{function (selectedTab) {
					return <>
						{
							selectedTab == "deactivated" ?
								<DisabledTab
									scenario={props.scenario}
									stakeholders={stakeholders}
									select={props.select}
									onEnableItem={(item) => {
										props.onChangeStakeholder(item.id, { enabled: true });
									}}
									onDeleteItem={(item) => {
										askDeleteConfirmation(item.id);
									}}
								/> :
								selectedTab == "export" ?
									<ExportTab>

									</ExportTab> :
									props.selection ? (
										<>
											<Flex>
												<div
													className="IconPicker"
													style={{ marginRight: '1rem' }}
												>
													<IconPicker
														value={
															props.selection?.icon ||
															'faUser'
														}
														favorites={allIcons}
														disabled={!!props.scenario || !uiContext.canEdit}
														onChange={
															props.scenario || !uiContext.canEdit
																? () => { }
																: (v) =>
																	props.onChangeStakeholder(
																		props.selection.id,
																		{ icon: v }
																	)
														}
													/>
												</div>

												<div>{props.selection?.name}</div>
											</Flex>

											{selectedTab == 'general' && (
												<GeneralTab
													selection={
														getItemAlteredByScenario(
															indexedStakeholders[props.selection?.id],
															props.scenario
														)
													}
													stakes={stakes}
													indicators={indicators}
													fishboneBranchs={props.fishboneBranchs}
													onEdit={handleEditStakeholder}
													onClone={(defaults) =>
														handleCreateStakeholder(
															{},
															defaults
														)
													}
													onDisable={async (id) => {
														await props.onChangeStakeholder(id, { enabled: false });
														props.deselect();
													}}
													onDelete={askDeleteConfirmation}
													scenario={props.scenario}
													displayIndicators={
														props.displayIndicators
													}
													displayStakes={
														props.displayStakes
													}
													stakeholders={stakeholders}
													indexedStakeholders={indexedStakeholders}
													onDeleteStakeholderLink={
														props.onDeleteStakeholderLink
													}
													onChangeStakeholderLinkStrength={
														props.onChangeStakeholderLinkStrength
													}
													onCreateLink={() =>
														handleChange('link')
													}
													select={props.select}
													risks={props.risks}

													tags={allTags}
													onUpdateStakeholderTags={props.onUpdateStakeholderTags}
													onCreateTag={props.onCreateTag}

													areTagsVisible={areTagsVisible}
													areIndicatorsVisible={areIndicatorsVisible}
													areStakesVisible={areStakesVisible}
													areRisksVisible={areRisksVisible}
													areOpportunitiesVisible={areOpportunitiesVisible}
													setPreferences={props.setPreferences}
												></GeneralTab>
											)}

											{selectedTab == 'links' && (
												<LinksTab
													indexedStakeholders={
														indexedStakeholders
													}
													stakeholders={stakeholders}
													onDeleteStakeholderLink={
														props.onDeleteStakeholderLink
													}
													onChangeStakeholderLinkStrength={
														props.onChangeStakeholderLinkStrength
													}
													onCreateLink={() =>
														handleChange('link')
													}
													selection={props.selection}
													scenario={props.scenario}
												></LinksTab>
											)}

											{selectedTab == 'fishbone' && (
												<FishboneLinksTab
													selection={props.selection}
													targetType="stakeholder"
													fishboneBranchs={props.fishboneBranchs}
													scenario={props.scenario}
													select={props.select}
													onClose={() => props.deselect()}
												/>
											)}

											{selectedTab == 'comments' && (
												<CommentsTab
													projectId={params.id}
													key={props.selection.id}
													type="Stakeholder"
													selection={props.selection}
													pushModal={props.pushModal}
													popModal={props.popModal}
													scenario={props.scenario}
												></CommentsTab>
											)}
										</>
									) : selectedTab == "bot" ? <>
										<BotTab
											projectId={params.id}
											messages={props.botThread?.messages || []}
											onResetThread={props.resetBotThread}
											onSendMessage={(message, callback) => props.addBotMessage(params.id, props.scenario?.id || null, "stakeholders", message, callback)}
										>

										</BotTab>
									</> :
										(
											<div className='noselection'>
												<Input
													onChange={(e) => {
														props.setSearchTerm(e.target.value);
													}}
													value={props.searchTerm}
													style={{
														marginLeft: 'auto',
														marginRight: '1em'
													}}
													placeholder={t('actions.search')}
													className="SearchInput mt-05"
												></Input>

												<ul>
													<li className="EditableListItem nomargin mt-1">
														<div style={{ textAlign: 'left' }}>
															{t('pages.navigator.links')}
														</div>
														<Weight
															count={1}
															onClick={() => {
																props.setPreferences(
																	'pages',
																	'navigator.links',
																	!areAllLinksVisible
																);
															}}
														>
															{areAllLinksVisible ? 1 : 0}
														</Weight>
													</li>
												</ul>

												<FoldableTagList
													title={t('pages.navigator.all_icons')}
													showAllTags={true}
													tags={allIcons.map(icon => ({
														id: icon, name: '', content: (
															<FontAwesomeIcon
																style={{ fontSize: '0.9rem' }}
																icon={fal[icon]}
															/>
														)
													}))}
													usedTags={keyBy(allIcons)}
													selectedTags={iconFilter ? [iconFilter] : []}
													emptyMessage={t('pages.navigator.no_icon')}
													onTagClick={(iconId) => {
														iconFilter === iconId
															? setIconFilter(null)
															: setIconFilter(iconId);
													}}
													className="icon-list mb-2"
												/>

												<FoldableTagList
													title={t('pages.navigator.all_tags')}
													tags={allTags}
													usedTags={usedTags}
													selectedTags={tagFilter}
													emptyMessage={t('pages.navigator.no_tag')}
													onTagClick={(tagId) => {
														// Add filter if not present, remove it if already present
														tagFilter.includes(tagId)
															? setTagFilter(tagFilter.filter(tag => tag !== tagId))
															: setTagFilter([...tagFilter, tagId]);
													}}
													className="mb-2"
												/>

												{props.displayStakes && (
													<FoldableTagList
														title={t('pages.navigator.all_stakes')}
														tags={sortBy(stakes, 'name')}
														usedTags={usedStakes}
														selectedTags={stakeFilter ? [stakeFilter] : []}
														emptyMessage={t('models.stake.no_x')}
														className="ImpactedStake mb-2"
														onTagClick={(stakeId) => {
															stakeFilter === stakeId
																? setStakeFilter(null)
																: setStakeFilter(stakeId);
															props.select(null);
														}}
													/>
												)}

												{props.displayRisks && (
													<>
														<FoldableTagList
															title={t('pages.navigator.all_risks')}
															tags={allRisks}
															usedTags={usedRisks}
															selectedTags={riskFilter ? [riskFilter] : []}
															emptyMessage={t('models.risk.messages.no_x')}
															className="ImpactedRisk mb-2"
															onTagClick={(riskId) => {
																riskFilter === riskId
																	? setRiskFilter(null)
																	: setRiskFilter(riskId);
																props.select(null);
															}}
														/>

														<FoldableTagList
															title={t('pages.navigator.all_opportunities')}
															tags={allOpportunities}
															usedTags={usedOpportunities}
															selectedTags={riskFilter ? [riskFilter] : []}
															emptyMessage={t('models.risk.messages.no_x_positive')}
															className="ImpactedRisk"
															onTagClick={(riskId) => {
																riskFilter === riskId
																	? setRiskFilter(null)
																	: setRiskFilter(riskId);
																props.select(null);
															}}
														/>
													</>
												)}
												{props.displayIndicators && (
													<FoldableTagList
														title={t('pages.navigator.all_constraints')}
														tags={sortBy(indicators, 'name')}
														usedTags={usedConstraints}
														selectedTags={constraintFilter ? [constraintFilter] : []}
														emptyMessage={t('models.indicator.no_x')}
														className="ImpactedIndicator mb-2"
														onTagClick={(indicatorId) => {
															constraintFilter === indicatorId
																? setConstraintFilter(null)
																: setConstraintFilter(indicatorId);
															props.select(null);
														}}
													/>
												)}

											</div>
										)
						}
					</>
				}}
			</SideMenu>

			{
				editedStakeholder && (
					<StakeholderModal
						onHide={() => {
							setEditedStakeholder(null);
						}}
						onSave={(values: Object) => {
							editedStakeholder.id
								? props.onChangeStakeholder(editedStakeholder.id, {
									...pick(editedStakeholder, [
										'name',
										'description',
										'impact',
										'trend',
										'icon'
									]),
									...values
								})
								: handleCreateStakeholder(
									{
										...values
									},
									{}
								);
							setEditedStakeholder(null);
						}}
						onDelete={() => {
							askDeleteConfirmation(editedStakeholder.id);
							setEditedStakeholder(null);
						}}
						id={editedStakeholder?.id}
						values={{
							name: editedStakeholder?.name,
							description: editedStakeholder?.description,
							impact: editedStakeholder?.impact
						}}
					></StakeholderModal>
				)
			}
		</Layout >
	);
};

export default Navigator;